import MediaLibraryClient from '../clients/MediaLibraryClient';
import MediaLibraryCollectionStorage from './storage-collection';
import MediaLibraryArchiveDownload from './download-archive';
import MediaLibraryAsset from './asset';
import MediaLibrarySearchSelection from './search-selection';
import MediaLibrarySearchPagination from './search-pagination';
import MediaLibraryEvents from './events';
import { displayErrorFeedbackMessage, displaySuccessFeedbackMessage } from '../utilities';
import MediaLibraryArchiveStorage from './storage-archive';

class MediaLibrarySearchComponent
{
    constructor()
    {
        // Imports
        this.mediaLibraryClient = new MediaLibraryClient();
        this.mediaLibraryCollectionStorage = new MediaLibraryCollectionStorage();
        this.mediaLibraryArchiveStorage = new MediaLibraryArchiveStorage();
        this.mediaLibraryAsset = new MediaLibraryAsset();
        this.mediaLibrarySelection = new MediaLibrarySearchSelection();
        this.mediaLibraryPagination = new MediaLibrarySearchPagination();
        this.mediaLibraryArchiveDownload = new MediaLibraryArchiveDownload();

        // Elements
        this.searchContainer = document.querySelector('.js-media-library__search--container');
        this.searchInput = document.querySelector('.js-media-library__search--input');
        this.searchButton = document.querySelector('.js-media-library__search--button');
        this.searchInfo = document.querySelector('.js-media-library__search--info');
        this.newFilterCheckbox = document.querySelector('.js-media-library__filter--new');
        this.newFilterCheckboxCounter = document.querySelector('.js-media-library__filter--new-counter');
        this.videoFilterCheckbox = document.querySelector('.js-media-library__filter--video');
        this.videoFilterCheckboxCounter = document.querySelector('.js-media-library__filter--video-counter');
        this.threeSixtyFilterCheckbox = document.querySelector('.js-media-library__filter--360');
        this.threeSixtyFilterCheckboxCounter = document.querySelector('.js-media-library__filter--360-counter');
        this.logoFilterCheckbox = document.querySelector('.js-media-library__filter--logo');
        this.logoFilterCheckboxCounter = document.querySelector('.js-media-library__filter--logo-counter');
        this.productNumberFilterButton = document.querySelector('.js-media-library__button--filter-product-number');
        this.newFilterButton = document.querySelector('.js-media-library__button--filter-new');
        this.videoFilterButton = document.querySelector('.js-media-library__button--filter-video');
        this.threeSixtyFilterButton = document.querySelector('.js-media-library__button--filter-360');
        this.logoFilterButton = document.querySelector('.js-media-library__button--filter-logo');
        this.resetFilterButton = document.querySelector('.js-media-library__button--filter-reset');
        this.addToCollectionButton = document.querySelector('.js-media-library__button--add-to-collection');
        this.tilesWrapper = document.querySelector('.js-media-library__tiles--container');
        this.tilesNoResults = document.querySelector('.js-media-library__tiles--no-results');
        this.resultCounter = document.querySelector('.js-media-library__result-counter');
        this.collectionLinkCounter = document.querySelector('.js-media-library__link-collection--counter');
        this.assetTemplate = document.querySelector('#media-library-tilesview-item-element-template').innerHTML;

        // Download Elements
        this.selectDownloadFormatsContainer =
            document.querySelector('.js-media-library__container--download-format');
        this.selectDownloadFormats =
            document.querySelectorAll('.js-media-library__checkbox--download-format');
        this.prepareSelectionButton =
            document.querySelector('.js-media-library__button--prepare-selection');
        this.prepareSelectionOverlayButton =
            document.querySelector('.js-media-library__button--prepare-selection--overlay-trigger');

        // Selectors
        this.tileSelector = ('.js-media-library__tile');
        this.checkboxSelector = ('.js-media-library__checkbox--choose');
        this.collectedTagSelector = ('.js-media-library__tag--collected');
        this.batchSearchCounterSelector = ('.js-media-library__button--filter-batch-counter');

        // Events
        this.tilesCreatedEvent = new Event(MediaLibraryEvents.tilesCreatedEvent);
        this.addToCollectionEvent = new Event(MediaLibraryEvents.addToCollectionEvent);
        this.assetSelectedEventType = MediaLibraryEvents.assetSelectedEvent;
        this.noAssetSelectedEventType = MediaLibraryEvents.noAssetSelectedEvent;

        // Texts
        this.batchSearchTagText = document
            .querySelector('meta[name="content.media.library.batch.search.tag"]')
            .getAttribute('content');
        this.addToCollectionFeedback = document
            .querySelector('meta[name="feedback.media.library.add.to.collection"]')
            .getAttribute('content');
        this.addToCollectionLimitReachedFeedback = document
            .querySelector('meta[name="feedback.media.library.add.to.collection.limit.reached"]')
            .getAttribute('content');

        // Helpers
        this.searchField = '';
        this.searchValue = '';
        this.isBatchSearch = false;
        this.productNumberBatch = [];
        this.eanBatch = [];
        this.isNewFilterSet = false;
        this.isVideoFilterSet = false;
        this.isThreeSixtyFilterSet = false;
        this.isLogoFilterSet = false;
        this.isNewFilterSetFromUrl = false;
        this.isVideoFilterSetFromUrl = false;
        this.isThreeSixtyFilterSetFromUrl = false;
        this.isLogoFilterSetFromUrl = false;
        this.assetsPerPage = 30;
        this.skipAssets = 0;
        this.customerId = orion.getCustomerId();
        this.downloadPath = 'https://downloads.orion-wholesale.com/prepared/';
        this.maxInputLength = 2000;
    }

    init()
    {
        let urlParams = this.checkUrlParameters();

        if (urlParams)
        {
            this.handleFilters(urlParams, true);
        }
        else
        {
            this.fetchAssets(this.assetsPerPage, this.skipAssets);
        }

        this.mediaLibrarySelection.initEvents();
        this.mediaLibraryArchiveDownload.init(
            this.getSelectedAssets.bind(this),
            this.enableActionButtons.bind(this),
            this.disableActionButtons.bind(this)
        );
        this.initCollectionCounter();
        this.initEvents();
    }

    initEvents()
    {
        this.searchInput.addEventListener('paste', event =>
        {
            const clipboardData = event.clipboardData.getData('text');
            const currentLength = this.searchInput.value.length;
            const resultLength = currentLength + clipboardData.length;

            if (resultLength > this.maxInputLength)
            {
                event.preventDefault();

                displayErrorFeedbackMessage(
                    'feedback.media.library.search.input.length.limit.reached',
                    '',
                    true,
                    false
                );
            }
        });

        this.searchInput.addEventListener('input', () =>
        {
            if (this.searchInput.value.length > this.maxInputLength)
            {
                this.searchInput.value = this.searchInput.value.slice(0, this.maxInputLength);

                displayErrorFeedbackMessage(
                    'feedback.media.library.search.input.length.limit.reached',
                    '',
                    true,
                    false
                );
            }

            this.searchInfo.classList.remove('mb--double-top');
        });

        this.searchInput.addEventListener('keypress', (e) =>
        {
            if (e.key === 'Enter')
            {
                e.preventDefault();
                this.checkInput() ? this.handleFilters('searchTerm', true) : null;
            }
        });

        this.searchButton.addEventListener('click', () =>
        {
            this.checkInput() ? this.handleFilters('searchTerm', true) : null;
        });

        this.newFilterCheckbox.addEventListener('click', () =>
        {
            this.handleFilters('newFilter', this.newFilterCheckbox.checked);
        });

        this.videoFilterCheckbox.addEventListener('click', () =>
        {
            this.handleFilters('videoFilter', this.videoFilterCheckbox.checked);
        });

        this.threeSixtyFilterCheckbox.addEventListener('click', () =>
        {
            this.handleFilters('threeSixtyFilter', this.threeSixtyFilterCheckbox.checked);
        });

        this.logoFilterCheckbox.addEventListener('click', () =>
        {
            this.handleFilters('logoFilter', this.logoFilterCheckbox.checked);
        });

        this.newFilterButton.addEventListener('click', () =>
        {
            this.handleFilters('newFilter', false);
        });

        this.videoFilterButton.addEventListener('click', () =>
        {
            this.handleFilters('videoFilter', false);
        });

        this.threeSixtyFilterButton.addEventListener('click', () =>
        {
            this.handleFilters('threeSixtyFilter', false);
        });

        this.logoFilterButton.addEventListener('click', () =>
        {
            this.handleFilters('logoFilter', false);
        });

        this.productNumberFilterButton.addEventListener('click', () =>
        {
            this.handleFilters('searchTerm', false);
        });

        this.resetFilterButton.addEventListener('click', () =>
        {
            this.resetAllFilters();
        });

        this.addToCollectionButton.addEventListener('click', () =>
        {
            this.addToCollection();
        });

        document.addEventListener('click', (e) =>
        {
            if (e.target !== this.searchInput && !this.searchButton.contains(e.target))
            {
                this.searchContainer.classList.remove('error', 'missing');
                this.searchInfo.classList.remove('mb--double-top');
            }
        });

        document.addEventListener(this.assetSelectedEventType, () =>
        {
            this.addToCollectionButton.classList.remove('disabled');
        });

        document.addEventListener(this.noAssetSelectedEventType, () =>
        {
            this.addToCollectionButton.classList.add('disabled');
        });

        document.addEventListener('tiles-created', () =>
        {
            this.checkAssetsInCollection();
        });

        document.addEventListener(MediaLibraryEvents.validateCollection, () =>
        {
            this.initCollectionCounter();
        });
    }

    /**
     * @return {Array} Selected asset data
     */
    getSelectedAssets()
    {
        return this.mediaLibrarySelection.getSelectedAssets();
    }

    /**
	 * @return {string | boolean} leading get param or false
	 */
    checkUrlParameters()
    {
        let url = new URL(window.location.href);
        let params = new URLSearchParams(url.search);

        if (params.size)
        {
            this.searchInput.value = params.has('searchTerm')
                ? params.get('searchTerm') : '';
            this.isNewFilterSetFromUrl = params.has('filters')
                ? params.get('filters').split(',').includes('new') : false;
            this.isVideoFilterSetFromUrl = params.has('filters')
                ? params.get('filters').split(',').includes('video') : false;
            this.isThreeSixtyFilterSetFromUrl = params.has('filters')
                ? params.get('filters').split(',').includes('360') : false;
            this.isLogoFilterSetFromUrl = params.has('filters')
                ? params.get('filters').split(',').includes('logo') : false;
            this.skipAssets = params.has('pageNumber')
                ? (parseInt(params.get('pageNumber')) - 1) * this.assetsPerPage : 0;

            return this.searchInput.value ? 'searchTerm'
                : this.isNewFilterSetFromUrl ? 'newFilter'
                    : this.isVideoFilterSetFromUrl ? 'videoFilter'
                        : this.isThreeSixtyFilterSetFromUrl ? 'threeSixtyFilter'
                            : this.isLogoFilterSetFromUrl ? 'logoFilter'
                                : this.skipAssets ? 'pagination'
                                    : false;
        }
        else
        {
            return false;
        }
    }

    /**
	 * @return {boolean} true if input is valid
	 */
    checkInput()
    {
        let filteredSearchValue = this.getFilteredSearchInputValue();
        let isSearchValueValid = filteredSearchValue.length >= 2;

        if (!isSearchValueValid)
        {
            this.searchContainer.classList.add('error', 'missing');
            this.searchInfo.classList.add('mb--double-top');
        }

        return isSearchValueValid;
    }

    /**
	 * @return {Array} asset ids in collection
	 */
    getCollection()
    {
        if (this.mediaLibraryCollectionStorage.hasCollection(this.customerId))
        {
            this.mediaLibraryCollectionStorage.restoreCollection(this.customerId);
            return this.mediaLibraryCollectionStorage.getCollection();
        }
    }

    initCollectionCounter()
    {
        let collection = this.getCollection();

        this.collectionLinkCounter.innerText = collection ? collection.length : 0;
    }

    checkAssetsInCollection()
    {
        let collection = this.getCollection();
        let tiles = document.querySelectorAll(this.tileSelector);

        tiles.forEach(tile =>
        {
            let checkbox = tile.querySelector(this.checkboxSelector);
            let { assetId } = checkbox.dataset;

            if (collection && collection.find(collectionId => collectionId === assetId))
            {
                tile.querySelector(this.collectedTagSelector).classList.remove('hidden');
            }
        });
    }

    /**
	 * @param {number} assetsPerPage page limit
	 * @param {number} skipAssets starting point
	 * @param {boolean} isPagination is pagination
	 */
    fetchAssets(assetsPerPage, skipAssets, isPagination = false)
    {
        this.tilesWrapper.innerText = '';

        this.skipAssets = skipAssets;

        if (!isPagination)
        {
            this.resetSelectedAssets();
        }

        if (this.isBatchSearch)
        {
            this.mediaLibraryClient.fetchAssetsByBatchSearch(
                this.eanBatch,
                this.productNumberBatch,
                this.isNewFilterSet,
                this.isVideoFilterSet,
                this.isThreeSixtyFilterSet,
                this.isLogoFilterSet,
                assetsPerPage,
                skipAssets
            )
                .then(response => response.json())
                .then(json =>
                {
                    if (json.totalItems !== 0 && json.items.length === 0)
                    {
                        this.handlePageOutOfBounds();
                    }
                    else if (json.totalItems !== 0)
                    {
                        this.handleResponse('batchSearch', json, skipAssets);
                    }
                    else
                    {
                        this.handleNoResults();

                        this.mediaLibrarySelection.selectAllButton.classList.add('hidden');
                    }
                });
        }
        else if (this.searchValue)
        {
            this.mediaLibraryClient.fetchAssetsBySearchTerm(
                this.searchField,
                this.searchValue,
                this.isNewFilterSet,
                this.isVideoFilterSet,
                this.isThreeSixtyFilterSet,
                this.isLogoFilterSet,
                assetsPerPage,
                skipAssets
            )
                .then(response => response.json())
                .then(json =>
                {
                    if (json.totalItems !== 0 && json.items.length === 0)
                    {
                        this.handlePageOutOfBounds();
                    }
                    else if (json.totalItems !== 0)
                    {
                        this.handleResponse('searchTerm', json, skipAssets);
                    }
                    else
                    {
                        if (this.searchField === 'productNumber' && this.searchValue.length === 11)
                        {
                            this.searchViaBaseNumber();
                        }
                        else
                        {
                            this.handleNoResults();

                            this.mediaLibrarySelection.selectAllButton.classList.add('hidden');
                        }
                    }
                });
        }
        else
        {
            this.mediaLibraryClient.fetchAllAssets(
                this.isNewFilterSet,
                this.isVideoFilterSet,
                this.isThreeSixtyFilterSet,
                this.isLogoFilterSet,
                assetsPerPage,
                skipAssets
            )
                .then(response => response.json())
                .then(json =>
                {
                    if (json.totalItems !== 0 && json.items.length === 0)
                    {
                        this.handlePageOutOfBounds();
                    }
                    else if (json.totalItems !== 0)
                    {
                        this.handleResponse('none', json, skipAssets);
                    }
                    else
                    {
                        this.handleNoResults();
                    }

                    this.mediaLibrarySelection.selectAllButton.classList.add('hidden');
                });
        }

        if (this.mediaLibraryArchiveStorage.checkStorage(this.customerId))
        {
            this.initDownload();
        }

        this.setUrlParams();

        this.isNewFilterSetFromUrl = false;
        this.isVideoFilterSetFromUrl = false;
        this.isThreeSixtyFilterSetFromUrl = false;
        this.isLogoFilterSetFromUrl = false;
        this.skipAssets = 0;
    }

    initDownload()
    {
        let storage = this.mediaLibraryArchiveStorage.getStorage(this.customerId);

        this.mediaLibraryClient.fetchProgressJson(this.downloadPath + storage.jsonPath)
            .then(() =>
            {
                this.selectDownloadFormats.forEach(checkbox =>
                {
                    checkbox.checked = storage.fileFormats.includes(checkbox.value);
                });

                this.mediaLibraryArchiveDownload.checkDownloadStatus(storage.jsonPath);
            })
            .catch(() =>
            {
                this.mediaLibraryArchiveStorage.removeStorage(this.customerId);
            });
    }

    setUrlParams()
    {
        let url = window.location.pathname;
        let params = new URLSearchParams();
        let searchTerm = this.isBatchSearch
            ? [...this.productNumberBatch, ...this.eanBatch].join(',')
            : this.searchValue;
        let filters = [
            this.isNewFilterSet ? 'new' : null,
            this.isVideoFilterSet ? 'video' : null,
            this.isThreeSixtyFilterSet ? '360' : null,
            this.isLogoFilterSet ? 'logo' : null
        ].filter(Boolean);
        let pageNumber = this.skipAssets / 30 + 1;

        if (searchTerm)
        {
            params.set('searchTerm', searchTerm);
        }
        else
        {
            params.delete('searchTerm');
        }

        if (filters.length > 0)
        {
            params.set('filters', filters.join(','));
        }
        else
        {
            params.delete('filters');
        }

        if (pageNumber > 1)
        {
            params.set('pageNumber', pageNumber.toString());
        }
        else
        {
            params.delete('pageNumber');
        }

        if (params.size)
        {
            url += '?' + params.toString();
        }

        history.replaceState({}, '', url);
    }

    removeUrlParams()
    {
        let newUrl = window.location.pathname;

        history.replaceState({}, '', newUrl);
    }

    handlePageOutOfBounds()
    {
        this.skipAssets = 0;

        this.fetchAssets(this.assetsPerPage, this.skipAssets);
    }

    /**
     * @param {string} searchType Type of search response to be handled
     * @param {object} json Asset data from the response
     * @param {number} skipAssets Assets to skip as starting point
     */
    handleResponse(searchType, json, skipAssets)
    {
        this.mediaLibraryAsset.initAssets(json, this.assetTemplate, this.tilesWrapper);

        this.resultCounter.innerText = json.totalItems;

        document.dispatchEvent(this.tilesCreatedEvent);

        if (searchType === 'batchSearch')
        {
            this.mediaLibrarySelection.setSelectAllData(
                'search',
                {
                    'eanBatch': this.eanBatch,
                    'productNumberBatch': this.productNumberBatch,
                    'newFilterSet': this.isNewFilterSet,
                    'videoFilterSet': this.isVideoFilterSet,
                    'threeSixtyFilterSet': this.isThreeSixtyFilterSet,
                    'logoFilterSet': this.isLogoFilterSet
                },
                json.totalItems
            );
        }
        else if (searchType === 'searchTerm')
        {
            this.mediaLibrarySelection.setSelectAllData(
                'search',
                {
                    'searchField': this.searchField,
                    'searchValue': this.searchValue,
                    'newFilterSet': this.isNewFilterSet,
                    'videoFilterSet': this.isVideoFilterSet,
                    'threeSixtyFilterSet': this.isThreeSixtyFilterSet,
                    'logoFilterSet': this.isLogoFilterSet
                },
                json.totalItems
            );
        }

        this.mediaLibraryPagination.init(this.fetchAssets.bind(this), json.totalItems, skipAssets);

        this.mediaLibrarySelection.initTiles();

        this.newFilterCheckboxCounter.innerText = json.aggregations.new;
        this.videoFilterCheckboxCounter.innerText = json.aggregations.videos;
        this.threeSixtyFilterCheckboxCounter.innerText = json.aggregations.archives;
        this.logoFilterCheckboxCounter.innerText = json.aggregations.logos;

        if (searchType !== 'none')
        {
            this.mediaLibrarySelection.selectAllButton.classList.remove('hidden');
        }
    }

    searchViaBaseNumber()
    {
        this.searchValue = this.searchValue.slice(0, 7);

        this.fetchAssets(this.assetsPerPage, this.skipAssets);
    }

    handleNoResults()
    {
        let clone = this.tilesNoResults.cloneNode(true);

        clone.classList.remove('hidden');

        this.tilesWrapper.append(clone);

        this.resultCounter.innerText = 0;

        this.newFilterCheckboxCounter.innerText = 0;
        this.videoFilterCheckboxCounter.innerText = 0;
        this.threeSixtyFilterCheckboxCounter.innerText = 0;
        this.logoFilterCheckboxCounter.innerText = 0;

        this.mediaLibraryPagination.init(this.fetchAssets.bind(this), 0);
    }

    /**
	 * @param {boolean} isActive enable or disable product number filter
	 */
    handleSearchTerm(isActive)
    {
        if (isActive)
        {
            this.isBatchSearch = this.determineBatchSearch();

            if (this.isBatchSearch)
            {
                this.prepareBatchSearch();

                let batchSearchLength = this.eanBatch.length + this.productNumberBatch.length;

                this.productNumberFilterButton.querySelector('span').innerHTML = this.batchSearchTagText;
                document.querySelector(this.batchSearchCounterSelector).innerText = '(' + batchSearchLength + ')';
            }
            else
            {
                this.prepareSingleSearch();
                this.productNumberFilterButton.querySelector('span').innerText = this.searchValue;
            }

            this.productNumberFilterButton.classList.remove('hidden');
            this.searchInput.value = '';

            this.handleNewFilter(this.isNewFilterSetFromUrl);
            this.handleVideoFilter(this.isVideoFilterSetFromUrl);
            this.handleThreeSixtyFilter(this.isThreeSixtyFilterSetFromUrl);
            this.handleLogoFilter(this.isLogoFilterSetFromUrl);
        }
        else
        {
            this.searchValue = '';
            this.eanBatch = [];
            this.productNumberBatch = [];
            this.productNumberFilterButton.querySelector('span').innerText = '';
            this.productNumberFilterButton.classList.add('hidden');
        }
    }

    /**
     * @return {boolean} whether the search value contains delimiters
     */
    determineBatchSearch()
    {
        let searchValue = this.searchInput.value.replace(/[^a-zA-Z0-9äöüÄÖÜß,;]/g, ' ').trim();
        let batchSearchPattern = /^[0-9\s,;]+$/;
        let isBatchSearchValid = batchSearchPattern.test(searchValue);
        let containsDelimiters = searchValue.includes(',') || searchValue.includes(';');

        return isBatchSearchValid && containsDelimiters;
    }

    prepareBatchSearch()
    {
        let numbers = this.searchInput.value.split(/[,;]/).map(number => number.trim()).filter(number => number !== '');

        this.eanBatch = [];
        this.productNumberBatch = [];

        numbers.forEach(number =>
        {
            if (number.length === 13)
            {
                this.eanBatch.push(number);
            }
            else
            {
                this.productNumberBatch.push(number);
            }
        });
    }

    prepareSingleSearch()
    {
        this.searchValue = this.searchInput.value;
        this.searchField = this.determineSearchField();
    }

    /**
	 * @return {string} search field
	 */
    determineSearchField()
    {
        if (!isNaN(this.searchValue))
        {
            if (this.searchValue.length === 13)
            {
                return 'eanCode';
            }
            else
            {
                return 'productNumber';
            }
        }
        else
        {
            return 'text';
        }
    }

    /**
     * @return {string} search input value without special characters
     */
    getFilteredSearchInputValue()
    {
        return this.searchInput.value.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, ' ').trim();
    }

    /**
	 * @param {boolean} isActive enable or disable new filter
	 */
    handleNewFilter(isActive)
    {
        if (isActive)
        {
            this.isNewFilterSet = true;
            this.newFilterCheckbox.checked = true;
            this.newFilterButton.classList.remove('hidden');
        }
        else
        {
            this.isNewFilterSet = false;
            this.newFilterCheckbox.checked = false;
            this.newFilterButton.classList.add('hidden');
        }
    }

    /**
	 * @param {boolean} isActive enable or disable video filter
	 */
    handleVideoFilter(isActive)
    {
        if (isActive)
        {
            this.isVideoFilterSet = true;
            this.videoFilterCheckbox.checked = true;
            this.videoFilterButton.classList.remove('hidden');
        }
        else
        {
            this.isVideoFilterSet = false;
            this.videoFilterCheckbox.checked = false;
            this.videoFilterButton.classList.add('hidden');
        }
    }

    /**
	 * @param {boolean} isActive enable or disable threeSixty filter
	 */
    handleThreeSixtyFilter(isActive)
    {
        if (isActive)
        {
            this.isThreeSixtyFilterSet = true;
            this.threeSixtyFilterCheckbox.checked = true;
            this.threeSixtyFilterButton.classList.remove('hidden');
        }
        else
        {
            this.isThreeSixtyFilterSet = false;
            this.threeSixtyFilterCheckbox.checked = false;
            this.threeSixtyFilterButton.classList.add('hidden');
        }
    }

    /**
	 * @param {boolean} isActive enable or disable threeSixty filter
	 */
    handleLogoFilter(isActive)
    {
        if (isActive)
        {
            this.isLogoFilterSet = true;
            this.logoFilterCheckbox.checked = true;
            this.logoFilterButton.classList.remove('hidden');
        }
        else
        {
            this.isLogoFilterSet = false;
            this.logoFilterCheckbox.checked = false;
            this.logoFilterButton.classList.add('hidden');
        }
    }

    /**
	 * @param {string} filter filter to handle
	 * @param {boolean} isActive enable or disable given filter
	 */
    handleFilters(filter, isActive)
    {
        switch (filter)
        {
        case 'searchTerm':
            this.handleSearchTerm(isActive);
            break;
        case 'newFilter':
            this.handleNewFilter(isActive);
            break;
        case 'videoFilter':
            this.handleVideoFilter(isActive);
            break;
        case 'threeSixtyFilter':
            this.handleThreeSixtyFilter(isActive);
            break;
        case 'logoFilter':
            this.handleLogoFilter(isActive);
            break;
        }

        if (
            this.searchValue === '' &&
            !this.eanBatch.length &&
            !this.productNumberBatch.length &&
            this.isNewFilterSet === false &&
            this.isVideoFilterSet === false &&
            this.isThreeSixtyFilterSet === false &&
            this.isLogoFilterSet === false
        )
        {
            this.resetFilterButton.classList.add('hidden');
        }
        else
        {
            this.resetFilterButton.classList.remove('hidden');
        }

        this.fetchAssets(this.assetsPerPage, this.skipAssets);
    }

    resetAllFilters()
    {
        this.searchValue = '';
        this.eanBatch = [];
        this.productNumberBatch = [];
        this.isNewFilterSet = false;
        this.isVideoFilterSet = false;
        this.isThreeSixtyFilterSet = false;
        this.isLogoFilterSet = false;
        this.newFilterCheckbox.checked = false;
        this.videoFilterCheckbox.checked = false;
        this.threeSixtyFilterCheckbox.checked = false;
        this.logoFilterCheckbox.checked = false;

        this.newFilterButton.classList.add('hidden');
        this.videoFilterButton.classList.add('hidden');
        this.threeSixtyFilterButton.classList.add('hidden');
        this.logoFilterButton.classList.add('hidden');
        this.productNumberFilterButton.classList.add('hidden');
        this.resetFilterButton.classList.add('hidden');

        this.removeUrlParams();

        this.resetSelectedAssets();

        this.fetchAssets(this.assetsPerPage, this.skipAssets);
    }

    addToCollection()
    {
        const selection = this.getSelectedAssets();
        const collection = this.getCollection();
        const space = collection ? 1000 - collection.length : 1000;
        const delta = collection ?
            selection.filter(asset => !collection.includes(asset.assetId)).length : selection.length;

        if (space >= delta)
        {
            let selection = this.mediaLibrarySelection.getSelectedAssets();

            if (this.mediaLibraryCollectionStorage.hasCollection(this.customerId))
            {
                this.mediaLibraryCollectionStorage.restoreCollection(this.customerId);
            }

            selection.forEach(asset =>
            {
                this.mediaLibraryCollectionStorage.addToCollection(asset.assetId);
            });

            this.mediaLibraryCollectionStorage.saveCollection(this.customerId);

            this.initCollectionCounter();

            displaySuccessFeedbackMessage(this.addToCollectionFeedback);

            this.resetSelectedAssets();

            this.checkAssetsInCollection();

            document.dispatchEvent(this.addToCollectionEvent);
        }
        else
        {
            displayErrorFeedbackMessage(this.addToCollectionLimitReachedFeedback);
        }
    }

    resetSelectedAssets()
    {
        this.mediaLibrarySelection.resetSelectedAssets();

        this.mediaLibrarySelection.deselectAllAssets();
    }

    enableActionButtons()
    {
        // ...
    }

    disableActionButtons()
    {
        this.prepareSelectionOverlayButton.classList.add('hidden');
        this.prepareSelectionButton.classList.add('hidden');
    }
}

if (document.querySelector('body').classList.contains('authorized')
    && document.querySelector('body').id === 'media-library-search')
{
    window.history.scrollRestoration = 'manual';

    const mediaLibrarySearch = new MediaLibrarySearchComponent();

    mediaLibrarySearch.init();
}
